import type {
  EdgeDeviceConfig,
  EdgeDeviceRoutingSettings,
  EdgeKioskConfig,
  EdgeRoutingRole,
  EdgeRoutingStationAssignment,
  EdgeRoutingZoneDefault,
  RestaurantEdgeSettings,
} from '@/api/types'

export type PrinterDefaults = {
  codePage: string
  qrEnabled: boolean
  qrSize: number
  qrEcLevel: string
}

export type PaymentCardDefaults = {
  zvtDefaults: {
    timeout3Ms: number
    timeout4Ms: number
    configByte: string
    cardReceiptPrint: string
    language: string
    forceRegistration: boolean
    delayBetweenCommandsMs: number
  }
  autoLogonOnStartup: boolean
  healthPollSeconds: number
  healthGateRouting: boolean
  useStub: boolean
  hobexEcr: {
    timeoutMs: number
  }
}

export type OpsBackups = {
  enabled: boolean
  dir: string
  dbPath: string
  cron: string
}

export const DEFAULT_KIOSK: EdgeKioskConfig = {
  stationId: 'KIOSK-01',
  zone: 'front',
  name: '',
}

export const DEFAULT_PRINTER: PrinterDefaults = {
  codePage: 'cp1252',
  qrEnabled: true,
  qrSize: 6,
  qrEcLevel: 'M',
}

export const DEFAULT_PAYMENT: PaymentCardDefaults = {
  zvtDefaults: {
    timeout3Ms: 5000,
    timeout4Ms: 180000,
    configByte: 'BE',
    cardReceiptPrint: 'ecr',
    language: 'DE',
    forceRegistration: true,
    delayBetweenCommandsMs: 0,
  },
  autoLogonOnStartup: false,
  healthPollSeconds: 30,
  healthGateRouting: true,
  useStub: false,
  hobexEcr: {
    timeoutMs: 7000,
  },
}

export const DEFAULT_OPS: OpsBackups = {
  enabled: true,
  dir: 'backups',
  dbPath: 'data/edge.db',
  cron: '0 5 2 * * *',
}

export const DEVICE_ROLES = [
  'printer.receipt',
  'printer.kitchen.hot',
  'printer.kitchen.cold',
  'payment.card',
  'payment.cash_recycler',
]

export const ROUTING_ROLES: Array<EdgeRoutingRole> = [
  'payment.card',
  'payment.cash_recycler',
  'printer.receipt',
  'printer.kitchen.hot',
]

export const ROUTING_ROLE_LABEL_KEYS: Record<EdgeRoutingRole, string> = {
  'payment.card': 'edgeSettings.routing.roles.paymentCard',
  'payment.cash_recycler': 'edgeSettings.routing.roles.cashRecycler',
  'printer.receipt': 'edgeSettings.routing.roles.receiptPrinter',
  'printer.kitchen.hot': 'edgeSettings.routing.roles.kitchenPrinter',
}

export const DEVICE_PROTOCOLS: Partial<Record<string, Array<string>>> = {
  'printer.receipt': ['tcp', 'http'],
  'printer.kitchen.hot': ['tcp', 'http'],
  'printer.kitchen.cold': ['tcp', 'http'],
  'payment.card': ['zvt', 'tecs'],
  'payment.cash_recycler': ['rest'],
}

export const EMPTY_DEVICE: EdgeDeviceConfig = {
  id: undefined,
  name: '',
  role: '',
  protocol: '',
  host: '',
  port: undefined,
  zones: [],
  allowedKiosks: [],
  priority: undefined,
  settings: undefined,
}

export const DEFAULT_ROUTING: Required<EdgeDeviceRoutingSettings> = {
  stationAssignments: [],
  zoneDefaults: [],
  fallbacks: [],
}

export function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object'
    ? (value as Record<string, unknown>)
    : {}
}

export function asString(value: unknown, fallback: string) {
  return typeof value === 'string' ? value : fallback
}

export function asNumber(value: unknown, fallback: number) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

export function asBoolean(value: unknown, fallback: boolean) {
  if (value === undefined || value === null) return fallback
  return Boolean(value)
}

export function findSetting(
  settings: RestaurantEdgeSettings['settings'],
  key: string,
) {
  return settings.find((item) => item.key === key)
}

export function parseDevices(devices: Array<EdgeDeviceConfig>) {
  return devices.map((d) => ({
    ...d,
    name: typeof d.name === 'string' ? d.name : '',
    zones: Array.isArray(d.zones) ? d.zones : [],
    allowedKiosks: Array.isArray(d.allowedKiosks) ? d.allowedKiosks : [],
    settings: parseDeviceSettings(d.settings),
  }))
}

export function parseDeviceSettings(value: unknown) {
  const settings = asRecord(value)
  const cardReceiptPrint = asString(settings.cardReceiptPrint, '').trim()
  const next: Record<string, string> = {}
  if (cardReceiptPrint) next.cardReceiptPrint = cardReceiptPrint
  if (Object.keys(next).length === 0) return undefined
  return next
}

export function parseKiosks(value: unknown) {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      const kiosk = asRecord(item)
      const stationId = asString(kiosk.stationId, DEFAULT_KIOSK.stationId)
      const zone = asString(kiosk.zone, DEFAULT_KIOSK.zone)
      const name = asString(kiosk.name, DEFAULT_KIOSK.name || '')
      return { stationId, zone, name }
    })
    .filter((k) => k.stationId.trim().length > 0)
}

export function normalizeKiosksForSave(list: Array<EdgeKioskConfig>) {
  return list
    .map((k) => ({
      stationId: (k.stationId || '').trim(),
      zone: (k.zone || '').trim(),
      name: (k.name || '').trim(),
    }))
    .filter((k) => k.stationId.length > 0 && k.zone.length > 0)
    .map((k) => (k.name ? k : { ...k, name: undefined }))
}

export function normalizeRoutingRole(value: unknown): EdgeRoutingRole | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return ROUTING_ROLES.includes(trimmed as EdgeRoutingRole)
    ? (trimmed as EdgeRoutingRole)
    : null
}

export function parseDeviceRouting(
  value: unknown,
): Required<EdgeDeviceRoutingSettings> {
  const raw = asRecord(value)

  const stationAssignments: Array<EdgeRoutingStationAssignment> = Array.isArray(
    raw.stationAssignments,
  )
    ? raw.stationAssignments
        .map((item) => {
          const assignment = asRecord(item)
          const stationId = asString(assignment.stationId, '').trim()
          const role = normalizeRoutingRole(assignment.role)
          const deviceId = asString(assignment.deviceId, '').trim()
          if (!stationId || !role || !deviceId) return null
          return { stationId, role, deviceId }
        })
        .filter((item): item is EdgeRoutingStationAssignment => item !== null)
    : []

  const zoneDefaults: Array<EdgeRoutingZoneDefault> = Array.isArray(
    raw.zoneDefaults,
  )
    ? raw.zoneDefaults
        .map((item) => {
          const zoneDefault = asRecord(item)
          const zone = asString(zoneDefault.zone, '').trim()
          const role = normalizeRoutingRole(zoneDefault.role)
          const deviceId = asString(zoneDefault.deviceId, '').trim()
          if (!zone || !role || !deviceId) return null
          return { zone, role, deviceId }
        })
        .filter((item): item is EdgeRoutingZoneDefault => item !== null)
    : []

  const fallbacks = Array.isArray(raw.fallbacks)
    ? raw.fallbacks
        .map((item) => {
          const fallback = asRecord(item)
          const role = normalizeRoutingRole(fallback.role)
          const deviceId = asString(fallback.deviceId, '').trim()
          if (!role || !deviceId) return null
          return { role, deviceId }
        })
        .filter(
          (
            item,
          ): item is NonNullable<
            Required<EdgeDeviceRoutingSettings>['fallbacks'][number]
          > => item !== null,
        )
    : []

  return {
    stationAssignments,
    zoneDefaults,
    fallbacks,
  }
}

export function normalizeRoutingForSave(
  routing: Required<EdgeDeviceRoutingSettings>,
  kiosks: Array<EdgeKioskConfig>,
  devices: Array<EdgeDeviceConfig>,
): Required<EdgeDeviceRoutingSettings> {
  const stationIds = new Set(kiosks.map((k) => (k.stationId || '').trim()))
  const zones = new Set(
    kiosks.map((k) => (k.zone || '').trim()).filter(Boolean),
  )
  const deviceIds = new Set(
    devices
      .map((d) => (typeof d.id === 'string' ? d.id.trim() : ''))
      .filter((id): id is string => id.length > 0),
  )

  const stationMap = new Map<string, EdgeRoutingStationAssignment>()
  for (const assignment of routing.stationAssignments) {
    const stationId = (assignment.stationId || '').trim()
    const role = normalizeRoutingRole(assignment.role)
    const deviceId = (assignment.deviceId || '').trim()
    if (!stationId || !role || !deviceId) continue
    if (!stationIds.has(stationId)) continue
    if (!deviceIds.has(deviceId)) continue
    stationMap.set(`${stationId}::${role}`, { stationId, role, deviceId })
  }

  const zoneMap = new Map<string, EdgeRoutingZoneDefault>()
  for (const zoneDefault of routing.zoneDefaults) {
    const zone = (zoneDefault.zone || '').trim()
    const role = normalizeRoutingRole(zoneDefault.role)
    const deviceId = (zoneDefault.deviceId || '').trim()
    if (!zone || !role || !deviceId) continue
    if (!zones.has(zone)) continue
    if (!deviceIds.has(deviceId)) continue
    zoneMap.set(`${zone}::${role}`, { zone, role, deviceId })
  }

  const fallbackMap = new Map<
    EdgeRoutingRole,
    Required<EdgeDeviceRoutingSettings>['fallbacks'][number]
  >()
  for (const fallback of routing.fallbacks) {
    const role = normalizeRoutingRole(fallback.role)
    const deviceId = (fallback.deviceId || '').trim()
    if (!role || !deviceId) continue
    if (!deviceIds.has(deviceId)) continue
    fallbackMap.set(role, { role, deviceId })
  }

  return {
    stationAssignments: Array.from(stationMap.values()).sort((a, b) =>
      `${a.stationId}::${a.role}`.localeCompare(`${b.stationId}::${b.role}`),
    ),
    zoneDefaults: Array.from(zoneMap.values()).sort((a, b) =>
      `${a.zone}::${a.role}`.localeCompare(`${b.zone}::${b.role}`),
    ),
    fallbacks: Array.from(fallbackMap.values()).sort((a, b) =>
      a.role.localeCompare(b.role),
    ),
  }
}
